import React from "react";
import { AlertCircle, Loader2, Play } from "lucide-react";
import { Button, Typography } from "@cointervue/design-system";
import type { PlayerOverlayProps } from "./types";

export const PlayerOverlay: React.FC<PlayerOverlayProps> = React.memo(({
  isLoading,
  isBuffering,
  error,
  isPlaying,
  onPlay,
  onRetry,
}) => {
  if (isLoading) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10">
        <Loader2 className="w-10 h-10 text-brand-dark-orange animate-spin mb-3" />
        <Typography
          variant="bodySmall"
          weight="medium"
          className="text-brand-black"
        >
          Buffering interview stream...
        </Typography>
        <Typography variant="caption" className="text-brand-gray mt-1">
          Connecting to secure stream
        </Typography>
      </div>
    );
  }

  if (isBuffering) {
    return (
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
        <div className="p-3.5 rounded-full bg-black/60 backdrop-blur-md">
          <Loader2 className="w-8 h-8 text-white animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white p-6 text-center z-10">
        <div className="p-3.5 rounded-full bg-brand-light-red text-brand-error-red mb-3">
          <AlertCircle className="w-8 h-8" />
        </div>
        <Typography
          variant="body"
          weight="semibold"
          className="text-brand-black mb-1"
        >
          Unable to stream recording
        </Typography>
        <Typography variant="caption" className="text-brand-gray mb-4 max-w-md">
          {error || "The recording video stream is currently unreachable."}
        </Typography>
        <Button
          type="button"
          size="sm"
          onClick={onRetry}
          className="w-auto px-4 bg-brand-dark-orange hover:bg-brand-dark-orange/90 text-white font-semibold rounded-lg shadow"
        >
          Retry Streaming
        </Button>
      </div>
    );
  }

  if (!isPlaying) {
    return (
      <button
        type="button"
        onClick={onPlay}
        className="absolute p-4 rounded-full bg-brand-dark-orange/90 hover:bg-brand-dark-orange text-white shadow-xl hover:scale-110 transition-all duration-200 z-10 cursor-pointer"
        aria-label="Play recording"
      >
        <Play className="w-8 h-8 fill-current translate-x-0.5" />
      </button>
    );
  }

  return null;
});

PlayerOverlay.displayName = "PlayerOverlay";